/**
 * Режим «Сборщик» (ТЗ Беки, этап 1): внешний водитель свозит велики из-за
 * зоны катания на HUB-парковки за сдельную ставку.
 *
 * Лист: никаких `api/*` и очереди — экраны передают сюда уже прочитанное.
 *
 * ⚠ СБОРЩИК — НЕ УРЕЗАННЫЙ СКАУТ. Все `/v1/scout/*` ему отвечают 403, поэтому
 * сборщик без scout/staff/admin видит ТОЛЬКО свой режим (`app/App.tsx`), а не
 * скаутские экраны с красными ошибками. Staff и admin открывают режим для
 * проверки — сервер пускает их в `/v1/collector/*`.
 */
import { ApiError } from './http'
import type { TKey } from './collector-i18n'
import { distanceM, hasPosition, type LatLng } from './geo'

const FULL_ACCESS = ['scout', 'staff', 'admin']

/** Роль `collector` без единой роли, которой открыт скаутский ярус. */
export function isCollectorOnly(roles: readonly string[]): boolean {
  return roles.includes('collector') && !FULL_ACCESS.some((r) => roles.includes(r))
}

/** Кому сервер ответит на `/v1/collector/*` (скауту — 403). */
export function canOpenCollector(roles: readonly string[]): boolean {
  return ['collector', 'staff', 'admin'].some((r) => roles.includes(r))
}

/**
 * Потолок ожидания «Забрал»/«Вернул»/«Отменить». Сервер ждёт замок, как
 * rides при завершении поездки (бюджет устройства 95 с, всего до ~120 с), а
 * HTTP-таймаут BFF — 150 с. Клиент сдаётся позже сервера: ответ «замок не
 * сработал» полезнее, чем наше «не дождались».
 */
export const LOCK_WAIT_TIMEOUT_MS = 160_000

/** Ставка — цвет метки: рядом с зоной (500 ₸) и далеко (700 ₸). */
export type RateBand = 'near' | 'far'

/** Граница «далеко» по ставке — на случай, если `band` не пришёл. */
const FAR_RATE_KZT = 700

export function rateBand(task: { band: string; rateKzt: number }): RateBand {
  if (task.band === 'far' || task.band === 'near') return task.band
  return task.rateKzt >= FAR_RATE_KZT ? 'far' : 'near'
}

/**
 * Цвета ставок. Не красный и не зелёный: на скаутской карте ими уже говорят
 * «сломан» и «в линии», и сборщик, видевший ту карту, прочитал бы их так же.
 */
export const RATE_COLOR: Record<RateBand, string> = {
  near: '#facc15',
  far: '#c084fc',
}

/**
 * «6 300 ₸». Разряды — своими руками, а не `toLocaleString`: локаль движка
 * в WebView и в тестах разная, и разделитель (обычный или неразрывный
 * пробел) плавал бы между ними.
 */
export function formatKzt(n: number): string {
  const digits = String(Math.round(Math.abs(n)))
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0')
  return `${n < 0 ? '−' : ''}${grouped}\u00a0₸`
}

export type TaskSort = 'distance' | 'rate'

type Sortable = { state: string; rateKzt: number; lat: number; lng: number }

/**
 * Порядок списка заданий.
 *
 * Свои (бронь, в машине) — всегда сверху: это работа, которая уже идёт, и
 * искать её среди чужих точек сборщик не должен. Без своей позиции
 * сортировка по расстоянию оставляет серверный порядок — сервер сортирует
 * открытые по расстоянию от присланной точки.
 */
export function sortTasks<T extends Sortable>(
  tasks: readonly T[],
  by: TaskSort,
  me: LatLng | null,
): T[] {
  const dist = (t: T) =>
    me && hasPosition(t) ? distanceM(me, t) : Number.POSITIVE_INFINITY
  const own = (t: T) => (t.state === 'reserved' || t.state === 'picked' ? 0 : 1)
  return [...tasks].sort((a, b) => {
    const byOwn = own(a) - own(b)
    if (byOwn !== 0) return byOwn
    if (by === 'rate') return b.rateKzt - a.rateKzt || dist(a) - dist(b)
    if (!me) return 0
    return dist(a) - dist(b) || b.rateKzt - a.rateKzt
  })
}

/**
 * Сколько броней занято из `maxReservations`. Только `reserved`: велик в
 * машине (`picked`) слот у сервера уже не держит, и счётчик с ним показывал
 * бы «лимит исчерпан», когда сервер ещё даёт брать.
 */
export function countReservations(tasks: readonly { state: string }[]): number {
  return tasks.filter((t) => t.state === 'reserved').length
}

/** Секунды до конца брони. Истекла — 0; не задано или не разобралось — null. */
export function secondsLeft(until: string, nowMs: number): number | null {
  if (until === '') return null
  const end = Date.parse(until)
  if (Number.isNaN(end)) return null
  return Math.max(0, Math.floor((end - nowMs) / 1000))
}

export function formatCountdown(sec: number): string {
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = sec % 60
  const mm = String(m).padStart(2, '0')
  const ss = String(s).padStart(2, '0')
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`
}

/**
 * Шаг задания. `gone` — всё, что заданием уже не является (`returned`,
 * незнакомое состояние): кнопок у него нет, новое состояние на сервере не
 * должно превращаться в кнопку, которую сервер отвергнет.
 */
export type TaskStage = 'open' | 'reserved' | 'picked' | 'gone'

export function taskStage(state: string): TaskStage {
  return state === 'open' || state === 'reserved' || state === 'picked' ? state : 'gone'
}

export type ReturnResult = 'whole' | 'broken'

export function pickBody(args: { qrToken: string; me: LatLng; photoKey: string }) {
  return {
    qrToken: args.qrToken.trim(),
    lat: args.me.lat,
    lng: args.me.lng,
    photoKey: args.photoKey,
  }
}

export function returnBody(args: {
  qrToken: string
  me: LatLng
  photoKey: string
  result: ReturnResult
}) {
  return { ...pickBody(args), result: args.result }
}

export type CancelReason = 'not_found' | 'other'

/** Предел комментария — как у «Убрать из пула» (рун, не байт). */
export const CANCEL_COMMENT_MAX = 300

/** Тело «Отменить». Пустой комментарий не отправляется вовсе. */
export function cancelBody(reason: CancelReason, comment: string): Record<string, unknown> {
  const text = Array.from(comment.trim()).slice(0, CANCEL_COMMENT_MAX).join('')
  return text ? { reason, comment: text } : { reason }
}

/**
 * «Беру» закрыто: только у ночного сборщика вне окна. `windowOpen=false`
 * protojson опускает у дневного (и у старого сервера поля нет вовсе), поэтому
 * по самому флагу закрытым считать нельзя — только в паре с видом.
 */
export function isTakeBlocked(limits: { kind: string; windowOpen: boolean }): boolean {
  return limits.kind === 'night' && !limits.windowOpen
}

/**
 * Сколько возвратов за смену осталось до бонуса («с 11-го возврата… осталось
 * N»). `null` — прогрессии нет (`from` = 0) или бонус уже идёт.
 */
export function nightProgressLeft(a: { returns: number; from: number }): number | null {
  if (a.from <= 0) return null
  const left = a.from - a.returns
  return left > 0 ? left : null
}

const PAYOUT_KEYS: Record<string, TKey> = {
  accrued: 'payout_accrued',
  disputed: 'payout_disputed',
  rejected: 'payout_rejected',
  paid: 'payout_paid',
}

/** Статус выплаты возврата словами; пусто — «начислено» (старый сервер), незнакомое — нейтрально. */
export function payoutStatusKey(status: string): TKey {
  if (status === '') return 'payout_accrued'
  return PAYOUT_KEYS[status] ?? 'payout_unknown'
}

const DISPUTE_KEYS: Record<string, TKey> = {
  same_bike_repeat: 'dispute_same_bike_repeat',
  anomalous_shift: 'dispute_anomalous_shift',
  ride_after_return: 'dispute_ride_after_return',
  manual: 'dispute_manual',
}

/** Причины спора: незнакомые пропускаем — выдумывать текст по коду нельзя. */
export function disputeReasonKeys(reasons: readonly string[]): TKey[] {
  return reasons.flatMap((r) => (DISPUTE_KEYS[r] ? [DISPUTE_KEYS[r]] : []))
}

/** 403 COLLECTOR_BLOCKED — доступ сборщика приостановлен. */
export function isBlockedError(e: unknown): boolean {
  return e instanceof ApiError && e.reason === 'COLLECTOR_BLOCKED'
}
